"use client"

import type React from "react"

import { useParams } from "react-router-dom"
import { useEffect, useMemo, useState } from "react"
import {
    Box,
    Typography,
    Button,
    Menu,
    MenuItem,
    Link,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    Paper,
    IconButton,
    Tooltip,
    Skeleton,
    Avatar,
} from "@mui/material"
import { PlayArrow, Download, CalendarToday, AccessTime, VideoFile, AudioFile, ArrowBack } from "@mui/icons-material"
import { useRecordingHook } from "src/slices/recording/useRecordingHook"
import { useCorvusEventList } from "@emerald-works/react-event-bus-client"
import { fetchRecording } from "src/slices/recording/recordingSlice"

import type { IFile } from "./../interfaces/IMeeting"

const format = (date: Date, formatString: string) => {
    // Implement a proper date formatting logic or use a library like date-fns
    return date.toLocaleString(); // Example fallback
}

const RecordingDetail = () => {
    const { sk } = useParams() as { sk: string }
    const { recording } = useRecordingHook()
    const [url, setUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [urlContent, setUrlContent] = useState<string>("")
    const [fetchRecordingEvent, getPreSignedUrl] = useCorvusEventList([
        fetchRecording,
        {
            eventName: "Fetch Recording Link",
            onSuccess: async (data: any) => {
                const { url, file }: { url: string; file: IFile } = data
                setUrl(url)
                setLoading(false)
                if (file.file_extension.toLowerCase() === "txt") {
                    try {
                        const response = await fetch(url)
                        const text = await response.text()
                        setUrlContent(text)
                    } catch (error) {
                        setUrlContent("Failed to load content.")
                    }
                }
            },
        },
    ])

    useEffect(() => {
        if (!fetchRecordingEvent.isWorking) {
            console.log("Fetching recording event triggered", sk)
            fetchRecordingEvent.trigger({ sk })
        }
    }, [sk, fetchRecordingEvent])

    const [selectedFile, setSelectedFile] = useState<IFile | null>(null)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    if (fetchRecordingEvent.isWorking || !recording) {
        return (
            <Box sx={{ p: 4, maxWidth: 1200, mx: "auto" }}>
                <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={400} />
            </Box>
        )
    }

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchorEl(null)
    }

    const handleFileSelect = (file: IFile) => {
        setSelectedFile(file)
        handleMenuClose()
        setLoading(true)
        getPreSignedUrl.trigger({ sk: recording.sk, file })
    }

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), "PPP")
        } catch (e) {
            return dateString
        }
    }

    const formatTime = (dateString: string) => {
        try {
            return format(new Date(dateString), "p")
        } catch (e) {
            return dateString
        }
    }

    const getFileTypeIcon = (fileExtension: string) => {
        return fileExtension.toLowerCase() === "mp4" ? <VideoFile /> : <AudioFile />
    }

    const getFileTypeColor = (fileExtension: string) => {
        return fileExtension.toLowerCase() === "mp4" ? "primary" : "secondary"
    }

    const getFileTypeName = (fileExtension: string, recordingType: string) => {
        if (fileExtension.toLowerCase() === "mp4") return "Video"
        if (fileExtension.toLowerCase() === "m4a") return "Audio"
        return recordingType
    }
    const formatSnakeCaseToTitleCase = (recording_type: string) => {
        return recording_type
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ')
    }

    return (
        <Box
            sx={{
                p: { xs: 2, md: 4 },
                maxWidth: 1200,
                mx: "auto",
                bgcolor: "#f8f9fa",
                minHeight: "100vh",
            }}
        >
            <Button startIcon={<ArrowBack />} sx={{ mb: 3 }} variant="text" color="primary" href="/">
                Back to Recordings
            </Button>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                    color: "white",
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h4" fontWeight="bold" gutterBottom>
                            {recording.topic}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                            <CalendarToday sx={{ mr: 1, fontSize: 18 }} />
                            <Typography variant="body1">{formatDate(recording.start_time)}</Typography>
                            <AccessTime sx={{ ml: 2, mr: 1, fontSize: 18 }} />
                            <Typography variant="body1">{formatTime(recording.start_time)}</Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                        <Button
                            variant="contained"
                            onClick={handleMenuClick}
                            startIcon={<PlayArrow />}
                            sx={{
                                bgcolor: "rgba(255,255,255,0.2)",
                                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                                color: "white",
                                px: 3,
                                py: 1.5,
                                borderRadius: 8,
                            }}
                        >
                            Select Recording
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleMenuClose}
                            PaperProps={{
                                elevation: 3,
                                sx: {
                                    borderRadius: 2,
                                    maxWidth: 360,
                                    mt: 1,
                                },
                            }}
                        >
                            {recording.recording_files.map((file) => (
                                <MenuItem
                                    key={file.id}
                                    onClick={() => handleFileSelect(file)}
                                    sx={{
                                        py: 1.5,
                                        borderBottom: "1px solid rgba(0,0,0,0.05)",
                                        "&:last-child": { borderBottom: "none" },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                        {getFileTypeIcon(file.file_extension)}
                                        <Box sx={{ ml: 2 }}>
                                            <Typography variant="subtitle2">
                                                {`${formatSnakeCaseToTitleCase(file.recording_type)} - ${getFileTypeName(file.file_extension, file.recording_type)}`}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {file.recording_start.substring(11, 16)} - {file.recording_end.substring(11, 16)}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={file.file_extension.toUpperCase()}
                                            size="small"
                                            color={getFileTypeColor(file.file_extension)}
                                            sx={{ ml: "auto" }}
                                        />
                                    </Box>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Card sx={{ borderRadius: 2, overflow: "hidden", mb: 4 }}>
                    <Skeleton variant="rectangular" height={400} animation="wave" />
                    <CardContent>
                        <Skeleton variant="text" height={30} width="60%" />
                        <Skeleton variant="text" height={20} width="40%" />
                    </CardContent>
                </Card>
            ) : selectedFile && url ? (
                <Card sx={{ borderRadius: 2, overflow: "hidden", mb: 4, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" }}>
                    {selectedFile.file_extension.toLowerCase() === "mp4" && (
                        <Box sx={{ position: "relative", paddingTop: "56.25%", bgcolor: "#000" }}>
                            <video
                                controls
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <source src={url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    )}
                    {selectedFile.file_extension.toLowerCase() === "m4a" && (
                        <Box
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: "primary.main",
                                    mb: 3,
                                }}
                            >
                                <AudioFile sx={{ fontSize: 40 }} />
                            </Avatar>
                            <audio
                                controls
                                style={{
                                    width: "100%",
                                    maxWidth: 500,
                                }}>
                                <source src={url} type="audio/mp4" />
                                Your browser does not support the audio tag.
                            </audio>
                        </Box>
                    )}
                    {selectedFile.file_extension.toLowerCase() === "txt" && (
                        <Box
                            sx={{
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: "secondary.main",
                                    mb: 3,
                                }}
                            >
                                <Typography variant="h6" color="white">
                                    TXT
                                </Typography>
                            </Avatar>
                            <Box
                                component="textarea"
                                readOnly
                                value={urlContent}
                                style={{
                                    width: "100%",
                                    maxWidth: 500,
                                    height: 300,
                                    padding: 10,
                                    borderRadius: 4,
                                    border: "1px solid #ccc",
                                    resize: "none",
                                    overflow: "auto",
                                }}
                            />
                        </Box>
                    )}
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                                <Typography variant="h6">
                                    {`${formatSnakeCaseToTitleCase(selectedFile.recording_type)} - ${getFileTypeName(selectedFile.file_extension, selectedFile.recording_type)}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedFile.recording_start} - {selectedFile.recording_end}
                                </Typography>
                            </Box>
                            <Tooltip title="Download">
                                <IconButton component={Link} href={url} download color="primary">
                                    <Download />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </CardContent>
                </Card>
            ) : (
                <Card
                    sx={{
                        borderRadius: 2,
                        p: 6,
                        textAlign: "center",
                        bgcolor: "rgba(255,255,255,0.8)",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                >
                    <Box sx={{ mb: 3 }}>
                        <PlayArrow sx={{ fontSize: 60, color: "primary.main", opacity: 0.7 }} />
                    </Box>
                    <Typography variant="h6" gutterBottom>
                        Select a recording to play
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose from the available recordings using the "Select Recording" button above
                    </Typography>
                </Card>
            )}

            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "medium" }}>
                        Recording Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Meeting ID
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {recording.id.toString()}
                        </Typography>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                            Topic
                        </Typography>
                        <Typography variant="body1">{recording.topic}</Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Start Time
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            {formatDate(recording.start_time)} at {formatTime(recording.start_time)}
                        </Typography>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                            Available Files
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                            {recording.recording_files.map((file) => (
                                <Chip
                                    key={file.id}
                                    icon={getFileTypeIcon(file.file_extension)}
                                    label={`${formatSnakeCaseToTitleCase(file.recording_type)} - ${getFileTypeName(file.file_extension, file.recording_type)}`}
                                    variant="outlined"
                                    color={getFileTypeColor(file.file_extension)}
                                    onClick={() => handleFileSelect(file)}
                                    sx={{ mb: 1 }}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    )
}

export { RecordingDetail }
